import { resolveManagedModel, defaultManagedModel } from "../../environment";
import {
  type AIProviderId,
  availableEffortLevels,
  defaultChatModel,
  defaultFastModel,
  getAIProvider,
  managedAIProviderIdForModel,
} from "../../../shared/aiProviders";
import type { CredentialField } from "../../../shared/aiProviders/manifest";
import { effortLabel } from "../../lib/effort";
import { formatModelName } from "../../lib/formatModelName";
import { ConnectorSelect } from "../ConnectorSelect";
import type { AppInfo } from "../ConnectorRow";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { ProviderLogo } from "../ProviderLogo";
import { Text } from "../Text";
import { Textarea } from "../Textarea";
import { DropdownOpener } from "../Settings/shared";
import type { AIConfigDraft } from "./aiProviderDraft";
import { FormCard, ModalField, fullWidthStyle } from "./modalForm";
import { ProviderSelect } from "./ProviderSelect";

const monospaceStyle = { fontFamily: "ui-monospace, monospace" } as const;
const mantleApiOptions = ["OpenAI", "Anthropic"];

function CredentialInput({
  field,
  value,
  onChange,
  reusePrior,
  disabled,
}: {
  field: CredentialField;
  value: string;
  onChange: (value: string) => void;
  reusePrior: boolean;
  disabled: boolean;
}) {
  const description =
    field.sensitive && reusePrior
      ? `Leave blank to keep the current ${field.label.toLowerCase()}.`
      : field.description;
  const placeholder =
    field.sensitive && reusePrior ? "••••••••" : field.placeholder;

  return (
    <ModalField label={field.label} description={description}>
      {field.multiline ? (
        <Textarea
          right={null}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
          minRows={6}
          maxRows={12}
          disabled={disabled}
          style={monospaceStyle}
        />
      ) : (
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={field.placeholder}
          autoComplete={field.sensitive ? "off" : undefined}
          disabled={disabled}
          secret={field.sensitive}
          secretIsSet={reusePrior}
          style={fullWidthStyle}
        />
      )}
    </ModalField>
  );
}

function ModelSelect({
  value,
  options,
  blankLabel,
  managedDefault,
  disabled,
  onChange,
}: {
  value: string;
  options: string[];
  // Null when the provider requires a model: an unsaveable default would just
  // disable Save with no explanation.
  blankLabel: string | null;
  // The model a blank value runs, set when options are managed models.
  managedDefault?: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  // Keep a previously-saved model selectable even if it's no longer in the list.
  const allOptions =
    value && !options.includes(value) ? [value, ...options] : options;
  const managed = managedDefault != null;
  const logo = (model: string) =>
    managed ? (
      <ProviderLogo provider={managedAIProviderIdForModel(model)} size={16} />
    ) : null;
  const label = (model: string) => (managed ? formatModelName(model) : model);
  return (
    <Dropdown
      opener={
        <DropdownOpener type="button" disabled={disabled}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {logo(value || managedDefault || "")}
            <Text variant="text-regular">
              {value ? label(value) : blankLabel || "Choose model"}
            </Text>
          </span>
          <span style={{ opacity: 0.3 }}>
            <Icon name="chevron-down" size={12} />
          </span>
        </DropdownOpener>
      }
      align="bottom-left"
      sections={[
        {
          items: [
            ...(blankLabel
              ? [
                  {
                    type: "button" as const,
                    left: logo(managedDefault ?? ""),
                    label: blankLabel,
                    right:
                      value === "" ? (
                        <Icon
                          name="check"
                          size={12}
                          color="var(--text-secondary)"
                        />
                      ) : undefined,
                    onClick: () => onChange(""),
                  },
                ]
              : []),
            ...allOptions.map((model) => ({
              type: "button" as const,
              left: logo(model),
              label: label(model),
              right:
                model === value ? (
                  <Icon name="check" size={12} color="var(--text-secondary)" />
                ) : undefined,
              onClick: () => onChange(model),
            })),
          ],
        },
      ]}
    />
  );
}

type Props = {
  draft: AIConfigDraft;
  reusePrior: boolean;
  disabled: boolean;
  onChange: (draft: AIConfigDraft) => void;
  connectors: AppInfo[];
  selectedConnectorName: string | null;
  allowedModels: string[];
  connectorModelsRestricted: boolean;
  managedModelOptions: string[] | null;
  onConnectorChange: (connectedAppId: string | null) => void;
  onAddConnector: () => void;
  onProviderChange: ((provider: AIProviderId) => void) | null;
  managed: boolean;
};

export function AiProviderSettingsTab({
  draft,
  reusePrior,
  disabled,
  onChange,
  connectors,
  selectedConnectorName,
  allowedModels,
  connectorModelsRestricted,
  managedModelOptions,
  onConnectorChange,
  onAddConnector,
  onProviderChange,
  managed,
}: Props) {
  const draftProvider = getAIProvider(draft.provider);
  // For the managed provider, capabilities come from the chat model's
  // sub-provider, not the env provider (always Bedrock).
  const effortProvider = managed
    ? getAIProvider(resolveManagedModel(draft.chatModel || null).provider)
    : draftProvider;
  // The env provider and a 3B managed AI provider both pick from managed models.
  const managedModels = managedModelOptions != null;
  const managedDefaultModel = managed
    ? defaultManagedModel.chatModel
    : managedModelOptions?.[0];
  const effectiveChatModel = managed
    ? resolveManagedModel(draft.chatModel || null).chatModel
    : managedModels
      ? draft.chatModel || managedDefaultModel || ""
      : draft.chatModel.trim() || defaultChatModel(draft.provider);
  const managedFastModel = resolveManagedModel(
    managed ? draft.chatModel || null : effectiveChatModel
  ).fastModel;
  const availableEfforts = availableEffortLevels(
    effortProvider,
    effectiveChatModel
  );
  const usesConnector = draft.connectedAppId != null;
  const mantleApi =
    draft.provider === "awsBedrockMantle"
      ? "OpenAI"
      : draft.provider === "awsBedrockMantleAnthropic"
        ? "Anthropic"
        : null;

  const patch = (next: Partial<AIConfigDraft>) =>
    onChange({ ...draft, ...next });

  return (
    <FormCard>
      {onProviderChange && (
        <ModalField label="Provider">
          <ProviderSelect
            value={mantleApi ? "awsBedrockMantle" : draft.provider}
            disabled={disabled}
            onChange={onProviderChange}
          />
        </ModalField>
      )}

      {mantleApi && (
        <ModalField
          label="API"
          description="Choose the API that supports this provider’s models."
        >
          <ModelSelect
            value={mantleApi}
            options={mantleApiOptions}
            blankLabel={null}
            disabled={disabled}
            onChange={(api) =>
              patch({
                provider:
                  api === "Anthropic"
                    ? "awsBedrockMantleAnthropic"
                    : "awsBedrockMantle",
                chatModel: "",
                fastModel: "",
                effort: null,
              })
            }
          />
        </ModalField>
      )}

      {!managed &&
        draftProvider.credentialFields
          // The api key comes from a connector, never typed here.
          .filter(
            (field) =>
              field.key !== "apiKey" && !(field.sensitive && usesConnector)
          )
          .map((field) => (
            <CredentialInput
              key={field.key}
              field={field}
              value={draft.credentials[field.key] ?? ""}
              onChange={(value) =>
                patch({
                  credentials: { ...draft.credentials, [field.key]: value },
                })
              }
              reusePrior={reusePrior}
              disabled={disabled}
            />
          ))}

      {!managed && (
        <ModalField label="Connector">
          <ConnectorSelect
            apps={connectors}
            value={draft.connectedAppId}
            selectedName={selectedConnectorName}
            onChange={onConnectorChange}
            placeholder="Choose connector"
            addNew={[{ label: "New connector", onClick: onAddConnector }]}
          />
        </ModalField>
      )}

      <ModalField
        label="Chat model"
        description={
          managed
            ? "The model 3B-managed chats use by default. Users can still switch models per chat."
            : connectorModelsRestricted
              ? "Chats start on this model. Users can switch to any other model this connector allows."
              : draftProvider.requiresChatModel
                ? "Required. Use the model ID exposed by your endpoint."
                : "Leave blank for the provider default."
        }
      >
        {managedModels ? (
          <ModelSelect
            value={draft.chatModel}
            options={managedModelOptions}
            blankLabel={
              managedDefaultModel
                ? `3B default (${formatModelName(managedDefaultModel)})`
                : null
            }
            managedDefault={managedDefaultModel}
            disabled={disabled}
            onChange={(value) => patch({ chatModel: value })}
          />
        ) : connectorModelsRestricted ? (
          <ModelSelect
            value={draft.chatModel}
            options={allowedModels}
            blankLabel={
              draftProvider.requiresChatModel ? null : "Provider default"
            }
            disabled={disabled}
            onChange={(value) => patch({ chatModel: value })}
          />
        ) : (
          <Input
            value={draft.chatModel}
            onChange={(event) => patch({ chatModel: event.target.value })}
            placeholder={defaultChatModel(draft.provider)}
            disabled={disabled}
            style={fullWidthStyle}
          />
        )}
      </ModalField>

      <ModalField
        label="Fast model"
        description={
          managedModels
            ? "Used for lightweight background tasks; follows your chat model."
            : "Leave blank to use the chat model."
        }
      >
        {managedModels ? (
          <DropdownOpener type="button" disabled>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ProviderLogo
                provider={managedAIProviderIdForModel(managedFastModel)}
                size={16}
              />
              <Text variant="text-regular">
                {formatModelName(managedFastModel)}
              </Text>
            </span>
          </DropdownOpener>
        ) : connectorModelsRestricted ? (
          <ModelSelect
            value={draft.fastModel}
            options={allowedModels}
            blankLabel="Use chat model"
            disabled={disabled}
            onChange={(value) => patch({ fastModel: value })}
          />
        ) : (
          <Input
            value={draft.fastModel}
            onChange={(event) => patch({ fastModel: event.target.value })}
            placeholder={defaultFastModel(draft.provider)}
            disabled={disabled}
            style={fullWidthStyle}
          />
        )}
      </ModalField>

      {availableEfforts.length > 0 && (
        <ModalField
          label="Effort"
          description="Default effort for this model. Users can override it per chat."
        >
          <Dropdown
            opener={
              <DropdownOpener type="button" disabled={disabled}>
                <Text variant="text-regular">{effortLabel(draft.effort)}</Text>
                <span style={{ opacity: 0.3 }}>
                  <Icon name="chevron-down" size={12} />
                </span>
              </DropdownOpener>
            }
            align="bottom-left"
            sections={[
              {
                items: [
                  {
                    type: "button" as const,
                    left: null,
                    label: "Provider default",
                    right:
                      draft.effort == null ? (
                        <Icon
                          name="check"
                          size={12}
                          color="var(--text-secondary)"
                        />
                      ) : undefined,
                    onClick: () => patch({ effort: null }),
                  },
                  ...availableEfforts.map((level) => ({
                    type: "button" as const,
                    left: null,
                    label: effortLabel(level),
                    right:
                      level === draft.effort ? (
                        <Icon
                          name="check"
                          size={12}
                          color="var(--text-secondary)"
                        />
                      ) : undefined,
                    onClick: () => patch({ effort: level }),
                  })),
                ],
              },
            ]}
          />
        </ModalField>
      )}
    </FormCard>
  );
}
